import React, { useState, useCallback, useEffect } from "react";
import {
  Page,
  AccountConnection,
  Spinner,
  Icon,
  Text,
} from "@shopify/polaris";
import DataTable3 from "./Components/DataTable3";
import { useLocation, useNavigate } from "react-router-dom";
import ShopifyStoreDetails from "./ShopifyStoreDetails/ShopifyStoreDetails";
import StoreDetailSettings from "./StoreDetails/StoreDetailSettings";
import {
  SettingsIcon,
  DnsSettingsIcon,
  ProductListIcon,
  ConnectIcon,
} from "@shopify/polaris-icons";

const Settings = () => {
  const [connected, setConnected] = useState(true);
  const [loading, setLoading] = useState(true);
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [disable, setDisabled] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    fetchConnectionStatus();
  }, [location.search]);

  const fetchConnectionStatus = async (openSettings = false) => {
    try {
      const urlParams = new URLSearchParams(location.search);
      const paramValue = urlParams.get("shop");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/google/auth/info?shop=${paramValue}`,
      );
      const data = await response.json();
      if (
        data.data == null &&
        !data.error &&
        data.message !== "Google Token is expire"
      ) {
        const merchants = await fetch(
          `${process.env.REACT_APP_API_URL}/api/google/merchants?shop=${paramValue}`,
        );
        const merchantRes = await merchants.json();
        if (merchantRes.data) {
          const merchantInfo = merchantRes.data.integration_info;
          if (merchantInfo?.accountIdentifiers) {
            setLoading(false);
            navigate(`/merchant-selection?shop=${paramValue}`, {
              state: merchantInfo.accountIdentifiers,
            });
            return;
          }
        }
      }
      if (!data.error && data.data) {
        setMerchantInfo(data.data);
        setConnected(true);
        setLoading(false);
        openSettings && setSelected(2);
      } else if (data.message === "Plan is not active") {
        setDisabled(true);
        setConnected(false);
        setLoading(false);
        navigate(`/plan?shop=${paramValue}`);
      } else {
        setConnected(false);
        setDisabled(false);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching connection status:", error);
      setConnected(false);
      setLoading(false);
    }
  };

  const handleAction = useCallback(() => {
    if (!connected) {
      const urlParams = new URLSearchParams(location.search);
      const paramValue = urlParams.get("shop");
      const popup = window.open(
        `${process.env.REACT_APP_API_URL}/api/google/auth?shop=${paramValue}`,
        "googleAuthPopup",
        "width=600,height=700",
      );

      const popupTimer = setInterval(() => {
        if (popup?.closed) {
          fetchConnectionStatus(true);
          clearInterval(popupTimer);
        }
      }, 500);
    } else {
      setConnected(false);
    }
  }, [connected, location.search]);

  const buttonText = connected ? "Disconnect" : "Connect";
  const details = connected ? "Account connected" : "No account connected";
  const terms = connected ? null : (
    <p>
      By clicking <strong>Connect</strong>, you can connect your Google merchant
      account with the app.
    </p>
  );

  const tabs = [
    {
      id: "manage-products",
      content: "Manage Products",
      icon: ProductListIcon,
    },
    {
      id: "shopify-settings",
      content: "Shopify Store Settings",
      icon: SettingsIcon,
    },
    {
      id: "merchant-settings",
      content: "Google Settings",
      icon: DnsSettingsIcon,
    },
  ];

  const renderProductsTab = () => (
    <Page fullWidth>
      {loading && (
        <div
          style={{ display: "flex", justifyContent: "center", padding: 40 }}
        >
          <Spinner accessibilityLabel="Loading products" size="large" />
        </div>
      )}
      {!loading && !connected && (
        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <span className="app-icon-tile app-icon-tile--blue">
                <Icon source={ConnectIcon} tone="base" />
              </span>
              <div>
                <Text variant="headingMd" as="h2">
                  Connect Google Merchant Center
                </Text>
                <Text as="p" tone="subdued">
                  Link your Google account before managing product feeds.
                </Text>
              </div>
            </div>
            <AccountConnection
              accountName=""
              connected={connected}
              title="Google Merchant Account"
              action={{
                content: buttonText,
                onAction: handleAction,
                disabled: disable,
              }}
              details={details}
              termsOfService={terms}
            />
          </div>
        </div>
      )}
      {!loading && connected && <DataTable3 merchantInfo={merchantInfo} />}
    </Page>
  );

  const tabContent = [
    renderProductsTab(),
    <ShopifyStoreDetails key="shopify" />,
    <StoreDetailSettings key="google" />,
  ];

  return (
    <div>
      <div className="app-page-hero">
        <div>
          <div className="app-page-hero__eyebrow">
            <Icon source={ProductListIcon} tone="base" />
            Workspace
          </div>
          <h1 className="app-page-hero__title">Manage Products</h1>
          <p className="app-page-hero__subtitle">
            Sync catalog items, tune Shopify defaults, and control Google feed
            settings from one place.
          </p>
        </div>
        <div className="app-page-hero__meta">
          <span
            className={`app-chip${
              connected ? " app-chip--success" : " app-chip--warn"
            }`}
          >
            {connected ? "Google connected" : "Google not connected"}
          </span>
        </div>
      </div>

      <div
        className="settings-tabs"
        role="tablist"
        aria-label="Product workspace"
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected === index}
            className={`settings-tab${selected === index ? " is-active" : ""}`}
            onClick={() => setSelected(index)}
          >
            <Icon source={tab.icon} tone="base" />
            <span>{tab.content}</span>
          </button>
        ))}
      </div>

      {tabContent[selected]}
    </div>
  );
};

export default Settings;
